import type { BollettaVista, Condominio, Vista } from "@/api/preventivo";
import { totaleCondomini } from "./preventivoFormato";

/** Una bolletta che la copia mostra: né non letta né col dettaglio non leggibile (decisione di Alfonso del 24/10). */
function mostrata(b: BollettaVista): boolean {
  return b.stato !== "non_letta" && b.dettaglio !== "non_leggibile";
}

/** I condomìni che si possono mettere nel link: quelli con almeno una bolletta da mostrare. */
export function condominiDelLink(v: Vista): Condominio[] {
  return v.condomini.filter((c) => c.contatori.some((k) => k.bollette.some(mostrata)));
}

/** Quante bollette sono ancora in lettura: in coda, alla prima lettura, o lette con la seconda lettura in corso. Una
 *  bolletta su più forniture conta una volta; non lette e gas non aspettano niente. Stessa regola del server
 *  (`app/preventivo/link.py`, `in_lettura`): finché non è zero il link non si crea (spec § 8). */
export function bolletteInLettura(v: Vista): number {
  const ids = new Set(v.bollette_fuori.filter((b) => b.stato === "in_coda" || b.stato === "in_lettura").map((b) => b.id));
  for (const c of v.condomini) {
    for (const k of c.contatori) {
      for (const b of k.bollette) {
        if (b.stato !== "non_letta" && b.dettaglio === "in_corso") ids.add(b.id);
      }
    }
  }
  return ids.size;
}

/** Quante bollette la copia per l'amministratore mostrerebbe: quelle dei condomìni, né non lette né col dettaglio non
 *  leggibile (decisione di Alfonso del 24/10; stessa regola del server, `da_escludere`). Zero: niente da mandare. */
export function bolletteNellaCopia(v: Vista): number {
  const ids = new Set<string>();
  for (const c of v.condomini) {
    for (const k of c.contatori) {
      for (const b of k.bollette) {
        if (mostrata(b)) ids.add(b.id);
      }
    }
  }
  return ids.size;
}

/** «21/11/2026»: il giorno in Italia. */
export function dataBreve(iso: string): string {
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" })
    .format(new Date(iso));
}

/** L'insieme con `chiave` tolta se c'era, aggiunta se non c'era. */
export function scambia(x: Set<string>, chiave: string): Set<string> {
  const y = new Set(x);
  if (!y.delete(chiave)) y.add(chiave);
  return y;
}

/** Le chiavi dei condomìni che ci sono ancora nell'elenco del link (giro 4). La pagina ci tiene i condomìni TOLTI dal
 *  link (29/10): un condominio che sparisce perde il suo stato, e se torna torna spuntato. Lo stesso insieme se non
 *  cambia nulla (nessun rendering in più). */
export function potaSpunte(spuntati: Set<string>, v: Vista): Set<string> {
  const ci = new Set(condominiDelLink(v).map((c) => c.chiave));
  const restano = [...spuntati].filter((k) => ci.has(k));
  return restano.length === spuntati.size ? spuntati : new Set(restano);
}

/** Nella copia per l'amministratore il totale «Tutti i condomìni» sta in cima solo con almeno due condomìni e una
 *  cifra: con uno solo ripeterebbe la riga del condominio. Quando c'è, il conteggio sotto il titolo non serve (giro 4). */
export function totaleInCima(condomini: Condominio[]): boolean {
  const t = totaleCondomini(condomini);
  return t.condomini >= 2 && t.mese !== null;
}
